import * as styles from '@/help/components/help-tabs-content-styles';
import { BinaryRenderer } from '@/widgets/components/binary-renderer';
import React, { useRef } from 'react';
import { cssVars } from '@/app/styles';
import styled from 'styled-components';
import {
  COOKIE_POLICY_CONTENT,
  POLICY_CONTENT,
  PRIVACY_POLICY_CONTENT,
  TERMS_POLICY_CONTENT,
} from '../policy-config';
import { PolicyNavTabs, PolicySidebar } from './policy-navs';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const HelpViewerWrapper = styled.div`
  display: flex;
  flex-grow: 100;
  flex-direction: column;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.li`
  padding: 4px;
  margin-top: 4px;
  margin-left: 12px;
  white-space: pre-line;
`;

const Header = styled.span`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
`;

const Subtitle = styled.span`
  height: 18px;
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  color: ${cssVars.textColors.color10};
  margin: 8px;
`;

const StyledParagraph = styled.p`
  margin: 8px;
  white-space: pre-line;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px;
`;

function ContentParagraphs(props) {
  return (
    <ContentWrapper>
      {props.content.map((item, index) => (
        <StyledParagraph key={index}>{item.text}</StyledParagraph>
      ))}
    </ContentWrapper>
  );
}

function ContentList(props) {
  return (
    <List>
      {props.content.map((item, index) => {
        // Allows text written in config file to use <b></b> to signify bold text
        const boldText = item.text.substr(
          item.text.indexOf('<b>') + 3,
          item.text.indexOf('</b>') - 3
        );
        const regularText =
          boldText !== ''
            ? item.text.substr(item.text.indexOf('</b>') + 4)
            : item.text;
        if (item.isListItem) {
          return (
            <ListItem key={index}>
              <span>
                <p>
                  {boldText !== '' && <b>{boldText}</b>}
                  {regularText}
                </p>
              </span>
            </ListItem>
          );
        }
        return <StyledParagraph key={index}>{item.text}</StyledParagraph>;
      })}
    </List>
  );
}

function PolicyContent(props) {
  return (
    <>
      {props.contentList.map((contentObj, index) => (
        <ContentWrapper key={index}>
          {contentObj.subtitle && <Subtitle>{contentObj.subtitle}</Subtitle>}
          {contentObj.isList ? (
            <ContentList content={contentObj.content} />
          ) : (
            <ContentParagraphs content={contentObj.content} />
          )}
        </ContentWrapper>
      ))}
    </>
  );
}

function PolicyContentHeader(props) {
  const { refs } = props;
  return (
    <styles.InnerWrapper
      ref={(ref) => {
        refs.current[props.header] = ref;
      }}
    >
      <Header data-cy="legal-header">{props.header}</Header>
      <PolicyContent contentList={props.contentList} />
    </styles.InnerWrapper>
  );
}

function PolicyContentDisplay(props) {
  return (
    <styles.HelpTabsContentWrapper ref={props.contentContainerRef}>
      <BinaryRenderer checkpoint={props.activeTab === POLICY_CONTENT.COOKIES}>
        {COOKIE_POLICY_CONTENT.map((item, index) => (
          <PolicyContentHeader
            key={index}
            header={item.header}
            contentList={item.contentList}
            refs={props.refs}
          />
        ))}
      </BinaryRenderer>
      <BinaryRenderer checkpoint={props.activeTab === POLICY_CONTENT.PRIVACY}>
        {PRIVACY_POLICY_CONTENT.map((item, index) => (
          <PolicyContentHeader
            key={index}
            header={item.header}
            contentList={item.contentList}
            refs={props.refs}
          />
        ))}
      </BinaryRenderer>
      <BinaryRenderer checkpoint={props.activeTab === POLICY_CONTENT.TERMS}>
        {TERMS_POLICY_CONTENT.map((item, index) => (
          <PolicyContentHeader
            key={index}
            header={item.header}
            contentList={item.contentList}
            refs={props.refs}
          />
        ))}
      </BinaryRenderer>
    </styles.HelpTabsContentWrapper>
  );
}

export function PolicyContentView(props) {
  const refs = useRef({});
  const contentContainerRef = useRef();
  return (
    <Wrapper>
      <PolicySidebar
        activeTab={props.activeContent}
        setActiveTab={props.setActiveContent}
        refs={refs}
        contentContainerRef={contentContainerRef}
        showReturnToLoginLink={props.showReturnToLoginLink}
        returnOnClick={() => props.setPolicyPageActive(false)}
      />
      <HelpViewerWrapper>
        <PolicyNavTabs
          activeTab={props.activeContent}
          onNavClick={(tab) => props.setActiveContent(tab)}
        />

        <PolicyContentDisplay
          refs={refs}
          activeTab={props.activeContent}
          contentContainerRef={contentContainerRef}
        />
      </HelpViewerWrapper>
    </Wrapper>
  );
}
